import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { 
  Building2, 
  Plus, 
  X, 
  Save,
  Briefcase,
  Languages,
  MapPin,
  Clock,
  DollarSign,
  Link as LinkIcon,
  Globe,
  Mail,
  Phone,
  Image,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const DiveCenterProfileTab = ({ user, token, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingRecruiting, setSavingRecruiting] = useState(false);
  const [linkedCenter, setLinkedCenter] = useState(null);
  
  // Dive center info (editable when linked)
  const [centerInfo, setCenterInfo] = useState({
    name: "",
    location: "",
    country: "",
    description: "",
    specialties: [],
    logo_url: "",
    website: "",
    email: "",
    phone: ""
  });
  
  // Recruiting profile
  const [profile, setProfile] = useState({
    recruiting: false,
    job_details: {
      qualifications: "",
      duration: "",
      salary: "",
      languages: [],
      description: ""
    }
  });
  
  const [newSpecialty, setNewSpecialty] = useState("");
  const [newJobLanguage, setNewJobLanguage] = useState("");

  useEffect(() => {
    if (user?.dive_center_profile) {
      setProfile({
        recruiting: user.dive_center_profile.recruiting || false,
        job_details: user.dive_center_profile.job_details || {
          qualifications: "",
          duration: "",
          salary: "",
          languages: [],
          description: ""
        }
      });
    }
    
    // Fetch linked dive center info
    if (user?.linked_dive_center_id) {
      fetchLinkedCenter();
    }
  }, [user]);

  const fetchLinkedCenter = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users/me/linked-dive-center`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        setLinkedCenter(res.data);
        setCenterInfo({
          name: res.data.name || "",
          location: res.data.location || "",
          country: res.data.country || "",
          description: res.data.description || "",
          specialties: res.data.specialties || [],
          logo_url: res.data.logo_url || "",
          website: res.data.website || "",
          email: res.data.email || "",
          phone: res.data.phone || ""
        });
      }
    } catch (error) {
      console.error("Failed to fetch linked center:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCenterInfo = async () => {
    setSavingInfo(true);
    try {
      await axios.put(`${API}/users/me/linked-dive-center`, centerInfo, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Dive center info updated!");
      fetchLinkedCenter();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update dive center info");
    } finally {
      setSavingInfo(false);
    }
  };

  const handleSaveRecruiting = async () => {
    setSavingRecruiting(true);
    try {
      await axios.put(`${API}/users/me/dive-center-profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Recruiting profile updated!");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingRecruiting(false);
    }
  };

  const addSpecialty = () => {
    if (!newSpecialty.trim()) return;
    if (centerInfo.specialties.includes(newSpecialty.trim())) {
      toast.error("Specialty already added");
      return;
    }
    setCenterInfo({
      ...centerInfo,
      specialties: [...centerInfo.specialties, newSpecialty.trim()]
    });
    setNewSpecialty("");
  };

  const removeSpecialty = (specialty) => {
    setCenterInfo({
      ...centerInfo,
      specialties: centerInfo.specialties.filter(s => s !== specialty)
    });
  };

  const addJobLanguage = () => {
    if (!newJobLanguage.trim()) return;
    const currentLangs = profile.job_details.languages || [];
    if (currentLangs.includes(newJobLanguage.trim())) {
      toast.error("Language already added");
      return;
    }
    setProfile({
      ...profile,
      job_details: {
        ...profile.job_details,
        languages: [...currentLangs, newJobLanguage.trim()]
      }
    });
    setNewJobLanguage("");
  };

  const removeJobLanguage = (language) => {
    setProfile({
      ...profile,
      job_details: {
        ...profile.job_details,
        languages: (profile.job_details.languages || []).filter(l => l !== language)
      }
    });
  };

  const languagePresets = ["English", "Spanish", "French", "German", "Italian", "Portuguese", "Chinese", "Japanese", "Arabic", "Russian", "Greek"];
  const specialtyPresets = ["PADI Courses", "SSI Courses", "Fun Diving", "Technical Diving", "Freediving", "Night Diving", "Wreck Diving", "Cave Diving", "Underwater Photography", "Island Hopping", "Snorkeling", "Equipment Rental"];

  if (loading) {
    return (
      <Card className="border-blue-200">
        <CardContent className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
          <p className="mt-2 text-gray-500">Loading dive center info...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Linked Dive Center Info */}
      <Card className="border-blue-200">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
          <CardTitle className="flex items-center gap-2 text-blue-700">
            <Building2 className="w-5 h-5" />
            Dive Center Information
          </CardTitle>
          <p className="text-sm text-gray-600">
            {linkedCenter 
              ? "Edit your dive center's public information"
              : "Your account is not yet linked to a dive center"
            }
          </p>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          {!linkedCenter ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
              <Building2 className="w-12 h-12 text-yellow-600 mx-auto mb-2" />
              <p className="text-yellow-800 font-medium">Not Linked Yet</p>
              <p className="text-sm text-yellow-700 mt-1">
                Please contact the admin to link your account to your dive center entry.
              </p>
            </div>
          ) : (
            <>
              {/* Logo Preview */}
              {centerInfo.logo_url && (
                <div className="flex justify-center">
                  <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 p-2 flex items-center justify-center">
                    <img 
                      src={centerInfo.logo_url.startsWith('http') ? centerInfo.logo_url : `${BACKEND_URL}${centerInfo.logo_url}`}
                      alt={centerInfo.name}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                </div>
              )}

              {/* Name */}
              <div>
                <Label htmlFor="dc-name">Dive Center Name</Label>
                <Input
                  id="dc-name"
                  value={centerInfo.name}
                  onChange={(e) => setCenterInfo({ ...centerInfo, name: e.target.value })}
                  placeholder="Your Dive Center Name"
                  className="mt-1"
                />
              </div>

              {/* Location & Country */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dc-location" className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    Location
                  </Label>
                  <Input
                    id="dc-location"
                    value={centerInfo.location}
                    onChange={(e) => setCenterInfo({ ...centerInfo, location: e.target.value })}
                    placeholder="e.g., Panglao, Bohol"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="dc-country">Country</Label>
                  <Input
                    id="dc-country"
                    value={centerInfo.country}
                    onChange={(e) => setCenterInfo({ ...centerInfo, country: e.target.value })}
                    placeholder="e.g., Philippines"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="dc-description">Description</Label>
                <Textarea
                  id="dc-description"
                  value={centerInfo.description}
                  onChange={(e) => setCenterInfo({ ...centerInfo, description: e.target.value })}
                  placeholder="Describe your dive center, facilities, and what makes you unique..."
                  rows={4}
                  className="mt-1"
                />
              </div>

              {/* Specialties */}
              <div>
                <Label className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  Services & Specialties
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {centerInfo.specialties.map((specialty) => (
                    <span
                      key={specialty}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                    >
                      {specialty}
                      <button onClick={() => removeSpecialty(specialty)} className="hover:text-blue-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <select
                    value={newSpecialty}
                    onChange={(e) => setNewSpecialty(e.target.value)}
                    className="flex-1 p-2 border rounded"
                  >
                    <option value="">Select or type specialty</option>
                    {specialtyPresets.filter(s => !centerInfo.specialties.includes(s)).map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <Button type="button" variant="outline" size="sm" onClick={addSpecialty}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dc-email" className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    Email
                  </Label>
                  <Input
                    id="dc-email"
                    type="email"
                    value={centerInfo.email}
                    onChange={(e) => setCenterInfo({ ...centerInfo, email: e.target.value })}
                    placeholder="contact@yourdivecenter.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="dc-phone" className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    Phone
                  </Label>
                  <Input
                    id="dc-phone"
                    value={centerInfo.phone}
                    onChange={(e) => setCenterInfo({ ...centerInfo, phone: e.target.value })}
                    placeholder="+1 234 567 8900"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Website */}
              <div>
                <Label htmlFor="dc-website" className="flex items-center gap-1">
                  <Globe className="w-4 h-4" />
                  Website
                </Label>
                <Input
                  id="dc-website"
                  value={centerInfo.website}
                  onChange={(e) => setCenterInfo({ ...centerInfo, website: e.target.value })}
                  placeholder="https://yourdivecenter.com"
                  className="mt-1"
                />
              </div>

              {/* Logo URL */}
              <div>
                <Label htmlFor="dc-logo" className="flex items-center gap-1">
                  <Image className="w-4 h-4" />
                  Logo URL
                </Label>
                <Input
                  id="dc-logo"
                  value={centerInfo.logo_url}
                  onChange={(e) => setCenterInfo({ ...centerInfo, logo_url: e.target.value })}
                  placeholder="/uploads/logo.png or https://..."
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Enter a URL to your logo image</p>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSaveCenterInfo}
                disabled={savingInfo}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {savingInfo ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Dive Center Info
                  </>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Recruiting Section */}
      <Card className="border-green-200">
        <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
          <CardTitle className="flex items-center gap-2 text-green-700">
            <Briefcase className="w-5 h-5" />
            Recruiting & Job Postings
          </CardTitle>
          <p className="text-sm text-gray-600">
            Toggle recruiting to attract dive professionals
          </p>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          {/* Recruiting Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Currently Recruiting</p>
              <p className="text-sm text-gray-500">
                {profile.recruiting 
                  ? "Your dive center is visible to professionals looking for work"
                  : "Enable to post job opportunities"
                }
              </p>
            </div>
            <Switch
              checked={profile.recruiting}
              onCheckedChange={(checked) => setProfile({ ...profile, recruiting: checked })}
            />
          </div>

          {/* Job Details (only if recruiting) */}
          {profile.recruiting && (
            <div className="space-y-4 p-4 border rounded-lg bg-green-50/50">
              <h3 className="font-medium text-green-800 flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                Job Posting Details
              </h3>

              {/* Qualifications */}
              <div>
                <Label htmlFor="qualifications">Required Qualifications</Label>
                <Textarea
                  id="qualifications"
                  value={profile.job_details.qualifications || ""}
                  onChange={(e) => setProfile({
                    ...profile,
                    job_details: { ...profile.job_details, qualifications: e.target.value }
                  })}
                  placeholder="e.g., PADI Divemaster or above, 100+ logged dives..."
                  rows={3}
                  className="mt-1"
                />
              </div>

              {/* Duration */}
              <div>
                <Label htmlFor="duration" className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Contract Duration
                </Label>
                <Input
                  id="duration"
                  value={profile.job_details.duration || ""}
                  onChange={(e) => setProfile({
                    ...profile,
                    job_details: { ...profile.job_details, duration: e.target.value }
                  })}
                  placeholder="e.g., 6 months, 1 year, Seasonal (May-October)"
                  className="mt-1"
                />
              </div>

              {/* Salary */}
              <div>
                <Label htmlFor="salary" className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  Compensation
                </Label>
                <Input
                  id="salary"
                  value={profile.job_details.salary || ""}
                  onChange={(e) => setProfile({
                    ...profile,
                    job_details: { ...profile.job_details, salary: e.target.value }
                  })}
                  placeholder="e.g., €1500/month + accommodation, Competitive + tips"
                  className="mt-1"
                />
              </div>

              {/* Languages Required */}
              <div>
                <Label className="flex items-center gap-1">
                  <Languages className="w-4 h-4" />
                  Languages Required
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(profile.job_details.languages || []).map((language) => (
                    <span
                      key={language}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                    >
                      {language}
                      <button onClick={() => removeJobLanguage(language)} className="hover:text-green-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <select
                    value={newJobLanguage}
                    onChange={(e) => setNewJobLanguage(e.target.value)}
                    className="flex-1 p-2 border rounded"
                  >
                    <option value="">Select language</option>
                    {languagePresets.filter(l => !(profile.job_details.languages || []).includes(l)).map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <Button type="button" variant="outline" size="sm" onClick={addJobLanguage}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Job Description */}
              <div>
                <Label htmlFor="job-description">Additional Details</Label>
                <Textarea
                  id="job-description"
                  value={profile.job_details.description || ""}
                  onChange={(e) => setProfile({
                    ...profile,
                    job_details: { ...profile.job_details, description: e.target.value }
                  })}
                  placeholder="Describe the role, work environment, benefits, and what makes your dive center special..."
                  rows={4}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleSaveRecruiting}
            disabled={savingRecruiting}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            {savingRecruiting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Recruiting Settings
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default DiveCenterProfileTab;
